RivalIQ.apps.Insights.module('widgets', function (widgets, App, Backbone, Marionette, $, _, d3, moment) {

    'use strict';

    const MOST_ACTIVE_SUM_MARGIN = 0.1;

    const FollowerOnlineTimesGraph = RivalIQ.graph.Line.extend({
        margin: {
            top: 30,
            right: 10,
            bottom: 20
        },

        renderData: function (svg, data) {
            const me = this;
            const parentFn = RivalIQ.graph.Line.prototype.renderData;
            parentFn.call(me, svg, data);
            me._drawDayDividers(svg);
            me._drawDayLabels(svg);
        },

        _drawDayDividers: function (svg) {
            const me = this;
            const yMax = _.last(me.y.domain());
            const yExtra = yMax / me.yTicks / 2;

            const dividerPositions = _.times(6, function (i) {
                const day = 24 * (i + 1);
                return [
                    [day, yMax + yExtra],
                    [day, 0]
                ];
            });

            const lineGroup = svg.selectAll('.dividers-group')
                .data([dividerPositions]);

            lineGroup.enter()
                .append('g');

            lineGroup
                .attr('class', 'dividers-group');

            me._drawLineSegments(lineGroup, []);
        },

        _drawDayLabels: function (svg) {
            const me = this;
            const x0 = me.dimensions.width / 14;
            const dx = me.dimensions.width / 7;

            const labels = _.times(7, function (i) {
                if (me.dimensions.width < 560) {
                    return moment.utc().day(i).format('ddd');
                } else {
                    return moment.utc().day(i).format('dddd');
                }
            });

            const labelGroup = svg.selectAll('.day-label')
                .data(labels);

            labelGroup.enter()
                .append('text');

            labelGroup
                .attr('class', 'day-label')
                .attr('text-anchor', 'middle')
                .attr('transform', function (d, i) {
                    return `translate(${x0 + i * dx},-15)`;
                })
                .text(_.identity);
        }
    });

    const FollowerOnlineTimes = widgets.FollowerOnlineTimesChart = RivalIQ.apps.Dashboard.widgets.Chart.extend({
        template: '#insightsInstagramLabeledFollowerOnlineTimesChartTemplate',
        className: 'chart follower-online-times-chart',

        chartType: FollowerOnlineTimesGraph,

        loadData: function () {
            const me = this;
            const companyId = me.dashboardModel.getProp('companyId');

            return App.utils.constructInsightsCompanyDataFetch(me.getProps()).then(function (cd) {
                const company = cd.get(companyId).toJSON();
                const primaryRecord = RivalIQ.utils.companyDisplay.getSourcePrimaryRecord(RivalIQ.enums.sources.INSTAGRAM_INSIGHTS, company);
                me.data = primaryRecord.activeFollowerTimes;
            });
        },

        getTooltip: function () {
            const me = this;

            return {
                title: function (data, index) {
                    const model = me.dashboardModel.metrics.first();
                    return model.getDomainName(index);
                },
                content: function (data) {
                    const viewOptions = {
                        collection: new RivalIQ.collections.MetricDefinitions(me.dashboardModel.metrics.map(function (metric, i) {
                            const value = data[0].value[i];

                            return new RivalIQ.models.MetricDefinition(_.extend(metric.toJSON(), {
                                value: metric.format(value)
                            }));
                        }))
                    };

                    return new RivalIQ.apps.Dashboard.widgets.MetricSummaryTooltip(viewOptions).render().el.outerHTML;
                }
            };
        },

        getChartAttributes: function () {
            const me = this;

            return {
                metrics: me.dashboardModel.metrics,
                data: me.getGraphData(),
                ticks: 28,
                height: 230,
                minLeftMargin: 40,
                tooltip: me.getTooltip()
            };
        },

        getGraphData: function () {
            const me = this;
            return [_.map(_.flatten(me.data), function (val, i) {
                return [i, val];
            })];
        },

        serializeData: function () {
            const me = this;
            return {
                metricLabel: _.result(me.dashboardModel, 'getMetricLabel')
            };
        }
    });

    const FollowerOnlineTimesChart = RivalIQ.apps.Dashboard.models.Widget.extend({
        view: FollowerOnlineTimes,

        scheduledExportAdvancedSettingsEnabled: false,

        processProps: function () {
            const me = this;
            me.metrics = new RivalIQ.collections.MetricDefinitions({
                dataSource: 'instagramInsightsFollowerOnlineTimes',
                name: 'Online Followers',
                className: 'follower-online-times'
            });
        },

        getMetricLabel: function () {
            // TODO: put this in strings
            return 'Followers Online Per Hour';
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.FOLLOWER_ONLINE_TIMES, FollowerOnlineTimesChart);

    App.views.InstagramFollowerOnlineTimesLayout = RivalIQ.views.LayoutView.mixin({
        template: '#insightsInstagramFollowerOnlineTimesTemplate',

        regions: {
            chart: '.followers-online-chart'
        },

        ui: {
            noDataMsgContainer: '.insights-no-data-msg-container',
            noDataMsg: '.insights-no-data-msg',
            chartRegion: '.chart-container'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        // this function will never be called with an empty grouping.
        getMostActiveTimeForDaysGroup: function (grouping) {
            const hoursPerBucket = 2;
            const bucketedTotalActiveBy = _.reduce(grouping, function (memo, day) {
                if (day) {
                    memo = _.times(day.length / hoursPerBucket, function (i) {
                        let bucketSum = 0;
                        _.times(hoursPerBucket, function (j) {
                            bucketSum += day[(i * hoursPerBucket) + j];
                        });
                        bucketSum += (memo[i] || 0);
                        return bucketSum;
                    });
                }
                return memo;
            }, []);

            const mostActiveBucket = _.max(bucketedTotalActiveBy);
            const bucketIndex = _.findIndex(bucketedTotalActiveBy, function (hour) {
                return hour === mostActiveBucket;
            });

            if (bucketIndex === -1) {
                return '';
            }
            const bucketStart = (bucketIndex * hoursPerBucket);
            const bucketEnd = bucketStart + hoursPerBucket;
            return `${moment.utc().hour(bucketStart).format('ha')} - ${moment.utc().hour(bucketEnd).format('ha')}`;
        },

        loadData: function () {
            const me = this;
            const companyId = me.dashboardModel.getProp('companyId');

            return App.utils.constructInsightsCompanyDataFetch(me.dashboardModel.getProps()).then(function (cd) {
                const company = cd.get(companyId).toJSON();
                const primaryRecord = RivalIQ.utils.companyDisplay.getSourcePrimaryRecord(RivalIQ.enums.sources.INSTAGRAM_INSIGHTS, company);
                // this data is organized as a utc week that begins on Sunday
                me.timesData = primaryRecord.activeFollowerTimes;
                me.presenceData = primaryRecord.presence;
            });
        },

        onRenderLoaded: function () {
            const me = this;
            me.toggleViews();
        },

        toggleViews: function () {
            const me = this;
            if (me.emptyOrIncompleteData()) {
                let noDataText = 'Your follower online times chart will display when we have enough data based on the last 30 days of activity.';

                const earliestAudienceDataDate = me.presenceData.earliest_audience_data_date;
                if (earliestAudienceDataDate && moment.utc(me.getProp('timePeriod').mainPeriodEnd).isBefore(earliestAudienceDataDate)) {
                    noDataText = `No data in time periods prior to ${moment.utc(earliestAudienceDataDate).format('ll')} (when you connected Instagram Insights to Rival IQ).`;
                }

                me.ui.chartRegion.hide();
                me.ui.noDataMsg.text(noDataText);
                me.ui.noDataMsgContainer.show();
            } else {
                me.showChart();
            }
        },

        showChart: function () {
            const me = this;
            me.chart.showWidget(me.dashboardModel.getWidget('insta-online-times'));
        },

        templateHelpers: function () {
            const me = this;
            return {
                summaryText: me.composeSummaryText()
            };
        },

        emptyOrIncompleteData: function () {
            const me = this;
            const emptyData = _.isEmpty(_.compact(_.flatten(me.timesData)));
            const incompleteData = _.some(me.timesData, function (dayOfWeek) { return _.isEmpty(_.compact(dayOfWeek)); });
            return emptyData || incompleteData;
        },

        composeSummaryText: function () {
            const me = this;
            let summaryText = '';

            if (!me.emptyOrIncompleteData()) {
                // find the most active days
                const totalActive = _.map(me.timesData, function (day) {
                    // we are looking for a matching index later, so make sure we can differentiate identical sums
                    return {
                        sum: _.isEmpty(_.compact(day)) ? null : _.sum(day),
                        used: false
                    };
                });
                const sortedTotalActive = _.compact(_.orderBy(_.clone(totalActive), 'sum', 'asc'));

                // there can be any number of most active days, as long as each is @ least MOST_ACTIVE_SUM_MARGIN% more active than the least active day
                let splittingPoint = 1;
                while (splittingPoint < 7 && (sortedTotalActive[splittingPoint].sum < ((1 + MOST_ACTIVE_SUM_MARGIN) * sortedTotalActive[0].sum))) {
                    splittingPoint++;
                }
                const mostActiveTotals = _.slice(sortedTotalActive, splittingPoint);
                const mostActiveDays = _(mostActiveTotals)
                    .map(function (mostActive) {
                        const dayOfWeek = _.findIndex(totalActive, function (total) {
                            return total.sum === mostActive.sum && !total.used;
                        });
                        totalActive[dayOfWeek].used = true;
                        return dayOfWeek;
                    })
                    .orderBy(_.identity, 'asc')
                    .map(function (dayOfWeek) {
                        return moment.utc().day(dayOfWeek).format('dddd');
                    })
                    .value();

                // now find the most active weekday time
                const weekdays = _.dropRight(_.drop(me.timesData));
                const mostActiveWeekdayTimes = me.getMostActiveTimeForDaysGroup(weekdays);

                // and the most active weekend time
                const weekends = [me.timesData[0], me.timesData[6]];
                const mostActiveWeekendTimes = me.getMostActiveTimeForDaysGroup(weekends);

                // compose day insight
                if (!_.isEmpty(mostActiveDays)) {
                    summaryText = 'Your followers are most active on ';
                    switch (mostActiveDays.length) {
                        case 1:
                            summaryText += $.tagHtml('strong', {
                                text: mostActiveDays[0]
                            });
                            break;
                        case 2:
                            summaryText += $.tagHtml('strong', {
                                text: `${mostActiveDays[0]} and ${mostActiveDays[1]}`
                            });
                            break;
                        default:
                            summaryText += $.tagHtml('strong', {
                                text: `${_.dropRight(mostActiveDays, 1).join(', ')}, and ${_.last(mostActiveDays)}`
                            });
                            break;
                    }
                    summaryText += '. ';
                }

                // compose times insight
                if (!_.isEmpty(summaryText)) {
                    summaryText += `They're most active between `;
                } else {
                    summaryText += 'Your followers are most active between ';
                }
                if (mostActiveWeekdayTimes === mostActiveWeekendTimes) {
                    return summaryText + $.tagHtml('strong', {
                        text: `${mostActiveWeekdayTimes} on all days.`
                    });
                } else {
                    return summaryText + $.tagHtml('strong', {
                        text: `${mostActiveWeekdayTimes} on weekdays and ${mostActiveWeekendTimes} on weekends.`
                    });
                }
            }
        }
    });
}, window.d3, window.moment);
